import { useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import ComboRegisterFormStep1 from "../../forms/combos/comboRegisterFormStep1";
import ComboRegisterFormStep2 from "../../forms/combos/comboRegisterFormStep2";
import ComboNutritionalInfoForm from "../../forms/combos/comboNutritionalInfoForm";
import Stepper from "../../../../components/lib/stepperHeader";

export const STEP = {
    BASIC: 0,
    DETAILS: 1,
    NUTRITION: 2,
    SUMMARY: 3,
};

const steps = [
    { key: STEP.BASIC, label: "Básico" },
    { key: STEP.DETAILS, label: "Detalhes" },
    { key: STEP.NUTRITION, label: "Tabela Nutricional" },
    { key: STEP.SUMMARY, label: "Resumo" },
];


const Info = ({ label, value }) => (
    <div className="flex justify-between border-b pb-1">
        <span className="font-medium">{label}</span>
        <span>{value || "-"}</span>
    </div>
);

const defaultNutrition = [
    { nutrient: "Valor Energético", valueInPortion: "", dailyValue: "" },
    { nutrient: "Carboidratos", valueInPortion: "", dailyValue: "" },
    { nutrient: "Proteínas", valueInPortion: "", dailyValue: "" },
    { nutrient: "Gorduras Totais", valueInPortion: "", dailyValue: "" },
    { nutrient: "Fibra Alimentar", valueInPortion: "", dailyValue: "" },
    { nutrient: "Sódio", valueInPortion: "", dailyValue: "" },
];

const parseNutritionalFacts = (data) => {
    if (!data) return defaultNutrition;
    try {
        const parsed = typeof data === 'string' ? JSON.parse(data) : data;
        return (Array.isArray(parsed) && parsed.length > 0) ? parsed : defaultNutrition;
    } catch (e) {
        return defaultNutrition;
    }
};

export const parseItemsList = (itemsList) => {
    if (!itemsList || typeof itemsList !== "string") return [];

    return itemsList
        .split(",")
        .map(item => item.trim())
        .filter(Boolean)
        .map(item => {
            const parts = item.split(" x");

            if (parts.length !== 3) return null;

            const [name, amount, type] = parts;

            return {
                name: name.trim(),
                amount: Number(amount),
                type: type.trim(),
            };
        })
        .filter(Boolean);
};


const ComboEditStepper = ({ combo, initialImagePreview, onFinish, onCancel, onDirty, isSubmitting }) => {
    const [step, setStep] = useState(STEP.BASIC);

    const formik = useFormik({
        initialValues: {
            // STEP 1
            imageFile: null,
            imagePreview: initialImagePreview || null,
            name: combo?.name || "",
            price: combo?.price || "",
            online_price: combo?.online_price ?? "",
            sku: combo?.sku || "",
            active: combo?.active ?? false,
            needsPreparation: combo?.needsPreparation ?? false,

            // STEP 2
            category: combo?.category || "",
            description: combo?.description || "",
            observations: combo?.observations || "",
            items: parseItemsList(combo?.itemsList),
            itemsList: combo?.itemsList || "",

            // STEP 3
            nutrition: parseNutritionalFacts(combo?.nutritionalFacts),
            client_code: combo?.clientCode
        },
        validationSchema: Yup.object({
            name: Yup.string().required("Nome é um campo obrigatório")
                .max(50, "O nome deve ter no máximo 50 caracteres"),
            price: Yup.number().required("Preço é um campo obrigatório"),
            sku: Yup.string()
                .max(30, 'O código SKU deve ter no máximo 30 caracteres')
                .trim('O campo não deve conter espaços no início ou fim')
                .matches(/^[\p{L}0-9()\-\s*#]*$/u, 'O campo não deve conter caracteres especiais \n Caracteres aceitos: letras, números, espaços, (), -, * e #'),
            imageFile: Yup.mixed()
                .nullable()
                .test("fileSize", "A foto do combo deve ter no máximo 25MB", (value) => {
                    if (!value) return true;
                    return value.size <= 25 * 1024 * 1024;
                })
                .test("fileType", "Formato inválido. Use JPEG, JPG ou PNG", (value) => {
                    if (!value) return true;
                    return ["image/jpeg", "image/png", "image/jpg"].includes(value.type);
                }),
        }),
        onSubmit: async (values) => {
            if (step < STEP.SUMMARY) {
                setStep(s => s + 1);
                return;
            }

            await onFinish({
                comboValues: values,
                image: values.imageFile,
            });
        }
    });

    return (
        <form onSubmit={formik.handleSubmit} onChange={onDirty}>
            <Stepper
                steps={steps}
                currentStep={step}
            />

            {step === STEP.BASIC && <ComboRegisterFormStep1 formik={formik} />}
            {step === STEP.DETAILS && <ComboRegisterFormStep2 formik={formik} />}
            {step === STEP.NUTRITION && (
                <ComboNutritionalInfoForm formik={formik} />
            )}
            {step === STEP.SUMMARY && (
                <div className="grid grid-cols-3 gap-6">
                    <div className="col-span-1">
                        {formik.values.imagePreview ? (
                            <img
                                src={formik.values.imagePreview}
                                className="rounded-xl object-cover w-full h-56"
                                alt=""
                            />
                        ) : (
                            <div className="h-56 flex items-center justify-center bg-gray-100 rounded-xl">
                                Sem imagem
                            </div>
                        )}
                        <div className="py-2">
                            <Info label="Nome" value={formik.values.name} />
                            <Info label="Preço" value={`R$ ${formik.values.price}`} />
                            <Info label="Preço Online" value={formik.values.online_price ? `R$ ${formik.values.online_price}` : ""} />
                        </div>
                    </div>

                    <div className="col-span-2 space-y-4 text-sm">
                        <Info label="SKU" value={formik.values.sku} />
                        <Info label="Ativo" value={formik.values.active ? "Sim" : "Não"} />
                        <Info label="Precisa preparo" value={formik.values.needsPreparation ? "Sim" : "Não"} />
                        <Info label="Categoria" value={formik.values.category} />
                        <Info label="Descrição" value={formik.values.description} />
                        <Info label="Observações" value={formik.values.observations} />
                    </div>

                    <div className="col-span-3 border rounded-xl p-4">
                        <h4 className="font-semibold mb-2">Lista de Itens</h4>
                        <div className="space-y-1">
                            {formik.values.items.map((item, idx) => (
                                <div key={idx} className="text-sm flex justify-between">
                                    <span>{item.name}</span>
                                    <span className="font-mono">x {item.amount}</span>
                                </div>
                            ))}
                            {formik.values.items.length === 0 && (
                                <p className="text-gray-400 text-sm">
                                    Nenhum item selecionado.
                                </p>
                            )}
                        </div>
                    </div>

                    <div className="col-span-3 border rounded-xl p-4">
                        <h4 className="font-semibold mb-2">Tabela Nutricional</h4>
                        {formik.values.nutrition.map((row, index) => (
                            <div key={index} className="text-sm">
                                <strong>{row.nutrient}</strong>: {row.valueInPortion} ({row.dailyValue}%)
                            </div>
                        ))}
                    </div>
                </div>
            )}

            <div className="flex justify-between mt-8">
                <button
                    type="button"
                    onClick={step === STEP.BASIC ? onCancel : () => setStep(step - 1)}
                    className="px-4 py-2 rounded-xl border"
                >
                    {step === STEP.BASIC ? "Cancelar" : "Voltar"}
                </button>

                <button
                    type="submit"
                    disabled={isSubmitting}
                    className="px-6 py-2 rounded-xl bg-blue-600 text-white"
                >
                    {step === STEP.SUMMARY ? "Concluir" : "Avançar"}
                </button>
            </div>
        </form>
    );
};

export default ComboEditStepper;
